import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MapPin, Play, Pause, Square, X, AlertCircle, Loader2, Trophy,
} from 'lucide-react';
import {
  checkGpsPermission, requestGpsPermission, startTracking, stopTracking,
  liveDistanceMeters, recentPaceSecPerUnit, finalizeSession,
  fmtPace, fmtDistance, fmtElapsed,
} from '../lib/gpsTracker';

/**
 * GpsTracker — full-screen overlay for live foreground GPS recording.
 *
 * Lifecycle: prompt → requesting → tracking ⇄ paused → confirm → complete
 *
 * Props:
 *   activity:  recovery activity record (for name + icon context)
 *   units:     'imperial' | 'metric'
 *   onComplete({ stats, samples, pauseSegments, startedAt, endedAt })
 *   onCancel()
 */
const GpsTracker = ({ activity, units = 'imperial', onComplete, onCancel }) => {
  const [phase, setPhase] = useState('prompt'); // prompt | denied | tracking | paused | confirm
  const [permError, setPermError] = useState(null);
  const [samples, setSamples] = useState([]);
  const [pauseSegments, setPauseSegments] = useState([]);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [signalAccuracy, setSignalAccuracy] = useState(null);

  const watchRef = useRef(null);
  const samplesRef = useRef([]);  // ref-mirror so the watch callback can append without stale closure
  const pauseRef = useRef([]);
  samplesRef.current = samples;
  pauseRef.current = pauseSegments;

  // Tick the elapsed display once per second
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const begin = useCallback(async () => {
    setPermError(null);
    const check = await checkGpsPermission();
    let perm = check;
    if (perm !== 'granted') {
      perm = await requestGpsPermission();
    }
    if (perm !== 'granted') {
      setPhase('denied');
      return;
    }
    try {
      const handle = await startTracking({
        onSample: (s) => {
          setSamples((prev) => [...prev, s]);
          if (s.ac != null) setSignalAccuracy(s.ac);
        },
        onError: (err) => {
          setPermError(err?.message || 'GPS error');
        },
      });
      watchRef.current = handle;
      setStartedAt(Date.now());
      setPhase('tracking');
    } catch (err) {
      setPermError(err?.message || 'Could not start GPS');
      setPhase('denied');
    }
  }, []);

  const pause = useCallback(() => {
    setPauseSegments((p) => [...p, [Date.now(), null]]);
    setPhase('paused');
  }, []);

  const resume = useCallback(() => {
    setPauseSegments((p) => {
      const out = p.slice();
      const last = out[out.length - 1];
      if (last && last[1] == null) last[1] = Date.now();
      return out;
    });
    setPhase('tracking');
  }, []);

  const stop = useCallback(async () => {
    // Close any still-open pause segment
    setPauseSegments((p) => {
      const out = p.slice();
      const last = out[out.length - 1];
      if (last && last[1] == null) last[1] = Date.now();
      return out;
    });
    if (watchRef.current) {
      await stopTracking(watchRef.current);
      watchRef.current = null;
    }
    setPhase('confirm');
  }, []);

  const cancel = useCallback(async () => {
    if (watchRef.current) {
      await stopTracking(watchRef.current);
      watchRef.current = null;
    }
    onCancel?.();
  }, [onCancel]);

  const confirmComplete = useCallback(() => {
    const endedAt = Date.now();
    const { record, rawPoints } = finalizeSession({
      samples: samplesRef.current,
      pauseSegments: pauseRef.current,
      startedAt,
      endedAt,
      units,
    });
    onComplete?.({ record, rawPoints, startedAt, endedAt });
  }, [onComplete, startedAt, units]);

  // Cleanup if user closes the overlay mid-track
  useEffect(() => () => { if (watchRef.current) { stopTracking(watchRef.current); } }, []);

  // ---- Live readouts ----
  const distanceM = liveDistanceMeters(samples, pauseSegments);
  const elapsed = startedAt ? now - startedAt - openPauseDuration(pauseSegments, now) : 0;
  const pace = recentPaceSecPerUnit(samples, pauseSegments, units, 30000);
  const sigColor = signalAccuracy == null
    ? 'text-enf-text-muted'
    : signalAccuracy <= 20 ? 'text-emerald-400'
    : signalAccuracy <= 50 ? 'text-amber-400'
    : 'text-enf-role-warning';
  const unitsLabel = units === 'metric' ? 'km' : 'mi';
  const paceLabel = units === 'metric' ? '/km' : '/mi';

  // ---- Render ----
  return (
    <div className="fixed inset-0 z-[60] bg-enf-surface-mid text-enf-text-primary overflow-y-auto" data-testid="gps-tracker-overlay">
      <div className="max-w-md mx-auto px-6 pt-10 pb-12 min-h-screen flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-enf-role-info text-[10px] uppercase tracking-[0.3em] font-semibold mb-1">Live GPS</div>
            <div className="font-display text-3xl tracking-wide leading-none">{activity?.name || 'Activity'}</div>
          </div>
          <button onClick={cancel} className="text-enf-text-muted hover:text-enf-text-primary transition-colors" data-testid="gps-tracker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {phase === 'prompt' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <MapPin className="w-12 h-12 text-enf-accent-fg mb-4" />
            <h2 className="font-display text-4xl tracking-wide leading-none mb-3">Track outdoors with GPS</h2>
            <p className="text-enf-text-secondary text-sm mb-8 max-w-xs">
              Live distance, pace, splits, and a route preview — all on your phone. Foreground only for now; keep your screen on or in your hand.
            </p>
            <button onClick={begin} className="px-6 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold flex items-center gap-2 transition-colors" data-testid="gps-tracker-start">
              <Play className="w-4 h-4" /> Start tracking
            </button>
            {permError && <div className="mt-4 text-xs text-enf-role-warning">{permError}</div>}
          </div>
        )}

        {phase === 'denied' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <AlertCircle className="w-12 h-12 text-enf-role-warning mb-4" />
            <h2 className="font-display text-3xl tracking-wide leading-none mb-3">Location permission needed</h2>
            <p className="text-enf-text-secondary text-sm mb-6 max-w-xs">
              GPS tracking needs location access. {permError ? `(${permError})` : ''} Enable it for Enferno in your device settings, then try again.
            </p>
            <div className="flex gap-3">
              <button onClick={cancel} className="px-5 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm">Cancel</button>
              <button onClick={begin} className="px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold">Try again</button>
            </div>
          </div>
        )}

        {(phase === 'tracking' || phase === 'paused') && (
          <div className="flex-1 flex flex-col">
            {/* Big stats stack */}
            <div className="space-y-6 mb-8">
              <Stat
                label="Time"
                value={fmtElapsed(elapsed)}
                muted={phase === 'paused'}
                testId="gps-time"
              />
              <Stat
                label={`Distance (${unitsLabel})`}
                value={fmtDistance(distanceM, units)}
                muted={phase === 'paused'}
                testId="gps-distance"
              />
              <Stat
                label={`Pace (${paceLabel})`}
                value={fmtPace(pace)}
                muted={phase === 'paused'}
                testId="gps-pace"
              />
            </div>

            {/* Signal + sample count */}
            <div className="flex items-center justify-between mb-8 px-2 text-xs">
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${sigColor.replace('text-', 'bg-')}`}></span>
                <span className="text-enf-text-muted">
                  GPS {signalAccuracy == null ? 'searching…' : `±${Math.round(signalAccuracy)}m`}
                </span>
              </div>
              <span className="text-enf-text-muted">{samples.length} samples</span>
            </div>

            {/* Controls */}
            <div className="mt-auto grid grid-cols-2 gap-3">
              {phase === 'tracking' ? (
                <button onClick={pause} className="py-4 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="gps-pause">
                  <Pause className="w-4 h-4" /> Pause
                </button>
              ) : (
                <button onClick={resume} className="py-4 bg-enf-role-info/10 hover:bg-enf-role-info/20 border border-enf-role-info/40 text-enf-role-info rounded-md font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="gps-resume">
                  <Play className="w-4 h-4" /> Resume
                </button>
              )}
              <button onClick={stop} className="py-4 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="gps-stop">
                <Square className="w-4 h-4" /> Finish
              </button>
            </div>
            {permError && <div className="mt-3 text-xs text-enf-role-warning text-center">{permError}</div>}
          </div>
        )}

        {phase === 'confirm' && (
          <ConfirmFinish
            samples={samples}
            pauseSegments={pauseSegments}
            startedAt={startedAt}
            units={units}
            onBack={() => setPhase('tracking')}
            onConfirm={confirmComplete}
          />
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, muted, testId }) => (
  <div className="text-center" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.3em] text-enf-text-muted font-semibold mb-2">{label}</div>
    <div className={`font-display text-7xl tracking-wide leading-none ${muted ? 'text-enf-text-muted' : 'text-enf-text-primary'}`}>{value}</div>
  </div>
);

const ConfirmFinish = ({ samples, pauseSegments, startedAt, units, onBack, onConfirm }) => {
  const endedAt = Date.now();
  const distanceM = liveDistanceMeters(samples, pauseSegments);
  const totalMs = endedAt - startedAt - openPauseDuration(pauseSegments, endedAt);
  const unitsLabel = units === 'metric' ? 'km' : 'mi';

  if (samples.length < 2 || distanceM < 5) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <Loader2 className="w-12 h-12 text-enf-role-info mb-4" />
        <h2 className="font-display text-3xl tracking-wide leading-none mb-3">Not enough data</h2>
        <p className="text-enf-text-secondary text-sm mb-6 max-w-xs">
          GPS captured fewer than a couple of points. Move to open sky for a clearer signal, then try again.
        </p>
        <button onClick={onBack} className="px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="text-center mb-8 mt-6">
        <div className="text-enf-role-info text-[10px] uppercase tracking-[0.3em] font-semibold mb-2">Session summary</div>
        <div className="font-display text-5xl tracking-wide leading-none">{fmtDistance(distanceM, units)} {unitsLabel}</div>
        <div className="text-enf-text-muted mt-2 text-sm">{fmtElapsed(totalMs)} · {samples.length} samples</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-auto">
        <button onClick={onBack} className="py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold" data-testid="gps-back-to-track">
          Back
        </button>
        <button onClick={onConfirm} className="py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="gps-confirm-finish">
          <Trophy className="w-4 h-4" /> Log session
        </button>
      </div>
    </div>
  );
};

// If a pause segment is still open (e.g. user paused but didn't resume), count
// the time from pause-start to `now` so the live timer doesn't keep ticking
// while paused.
const openPauseDuration = (pauseSegments, now) => {
  let total = 0;
  for (const [s, e] of pauseSegments) {
    if (s == null) continue;
    total += (e == null ? now : e) - s;
  }
  return total;
};

export default GpsTracker;
